import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, CreditCard, Edit2, Eye, Loader2, Package2, Plus, Search, ShoppingCart, Warehouse, XCircle } from "lucide-react";
import { getApiUrl } from "../../config/api";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

type ViewMode = "orders" | "inventory";

interface InventoryItem {
  id: number;
  name: string;
  description?: string | null;
  size?: string | null;
  buyingPrice?: number | null;
  sellingPrice?: number | null;
  price?: number | null;
  stock: number;
  category?: string | null;
}

interface StudentOption {
  id: number;
  name: string;
  email?: string;
  admissionNumber?: string;
  userCode?: string;
}

interface Order {
  id: number;
  studentName?: string;
  className?: string;
  item?: string;
  quantity?: number;
  price?: number;
  totalAmount?: number;
  status?: string;
  paymentStatus?: string;
  paidAmount?: number;
  balance?: number;
  paymentMethod?: string;
  createdAt?: string;
  approvalStatus?: string;
}

const getOrderStatusBadgeClass = (status?: string) => {
  const normalized = String(status || "").toLowerCase();

  if (normalized.includes("issued") || normalized === "completed") {
    return "bg-emerald-50 text-emerald-700 border-emerald-100";
  }
  if (normalized === "processing" || normalized === "ready") {
    return "bg-sky-50 text-sky-700 border-sky-100";
  }
  if (normalized === "cancelled") {
    return "bg-rose-50 text-rose-700 border-rose-100";
  }
  if (normalized === "pending") {
    return "bg-amber-50 text-amber-700 border-amber-100";
  }

  return "bg-slate-50 text-slate-600 border-slate-100";
};

const getPaymentStatusBadgeClass = (paymentStatus?: string) => {
  const normalized = String(paymentStatus || "").toLowerCase();

  if (normalized === "paid") {
    return "bg-emerald-50 text-emerald-700 border-emerald-100";
  }
  if (normalized === "partial") {
    return "bg-sky-50 text-sky-700 border-sky-100";
  }
  if (normalized === "unpaid" || normalized === "pending") {
    return "bg-rose-50 text-rose-700 border-rose-100";
  }

  return "bg-slate-50 text-slate-600 border-slate-100";
};

const hasReachedIssueThreshold = (order?: Order | null) => {
  if (!order) return false;
  const totalAmount = Number(order.totalAmount || 0);
  const paidAmount = Number(order.paidAmount || 0);
  if (totalAmount <= 0) return false;
  return paidAmount >= totalAmount / 2;
};

const UniformsManagement: React.FC = () => {
  const token = localStorage.getItem("token") || "";
  const [viewMode, setViewMode] = useState<ViewMode>("orders");
  const [searchTerm, setSearchTerm] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  useAutoDismiss(message, setMessage, null, 5000);

  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);

  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [itemForm, setItemForm] = useState({
    name: "",
    description: "",
    size: "",
    buyingPrice: "",
    sellingPrice: "",
    stock: "",
    category: ""
  });

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [activeOrderAction, setActiveOrderAction] = useState<{ orderId: number; type: "issuing" | "payment" } | null>(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    paymentMethod: "Cash",
    reference: "",
    notes: ""
  });
  const [students, setStudents] = useState<StudentOption[]>([]);
  const [isOfflineOrderModalOpen, setIsOfflineOrderModalOpen] = useState(false);
  const [creatingOfflineOrder, setCreatingOfflineOrder] = useState(false);
  const [offlineStudentSearch, setOfflineStudentSearch] = useState("");
  const [offlineOrderForm, setOfflineOrderForm] = useState({
    studentId: "",
    notes: "",
    items: [{ itemId: "", quantity: "1" }]
  });

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl("inventory"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventory(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      setMessage({ type: "error", text: err.response?.data?.message || "Failed to load inventory." });
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl("orders"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      setMessage({ type: "error", text: err.response?.data?.message || "Failed to load orders." });
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchStudents = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl("users/students"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const nextStudents = Array.isArray(res.data?.students) ? res.data.students : Array.isArray(res.data) ? res.data : [];
      setStudents(nextStudents);
    } catch (err: any) {
      try {
        const fallbackRes = await axios.get(getApiUrl("communications/teachers/learners"), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const fallbackStudents = Array.isArray(fallbackRes.data?.learners)
          ? fallbackRes.data.learners.map((learner: any) => ({
              id: learner.learnerId,
              name: learner.learnerName,
              email: learner.email,
              admissionNumber: learner.admissionNumber,
              userCode: learner.userCode
            }))
          : [];
        setStudents(fallbackStudents);
      } catch (fallbackErr: any) {
        setMessage({ type: "error", text: fallbackErr.response?.data?.message || err.response?.data?.message || "Failed to load students." });
      }
    }
  }, [token]);

  useEffect(() => {
    if (viewMode === "inventory") {
      fetchInventory();
    } else {
      fetchOrders();
    }
  }, [viewMode, fetchInventory, fetchOrders]);

  const filteredInventory = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return inventory;
    return inventory.filter((item) =>
      item.name.toLowerCase().includes(term) ||
      (item.description || "").toLowerCase().includes(term) ||
      (item.category || "").toLowerCase().includes(term) ||
      (item.size || "").toLowerCase().includes(term)
    );
  }, [inventory, searchTerm]);

  const parseItems = (raw?: string) => {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed;
    } catch {
      return [];
    }
  };

  const filteredOrders = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return orders;
    return orders.filter((order) =>
      (order.studentName || "").toLowerCase().includes(term) ||
      (order.className || "").toLowerCase().includes(term) ||
      (order.status || "").toLowerCase().includes(term)
    );
  }, [orders, searchTerm]);

  const visibleOrders = useMemo(() => {
    return filteredOrders.filter((order) => {
      if (orderStatusFilter === "all") return true;
      if (orderStatusFilter === "pending") return String(order.status || "").toLowerCase() === "pending";
      if (orderStatusFilter === "issued") return String(order.status || "").toLowerCase().includes("issued");
      if (orderStatusFilter === "approved") return String(order.approvalStatus || "").toLowerCase() === "approved";
      if (orderStatusFilter === "payment_pending") return String(order.paymentStatus || "").toLowerCase() !== "paid";
      return true;
    });
  }, [filteredOrders, orderStatusFilter]);

  const filteredOfflineStudents = useMemo(() => {
    const term = offlineStudentSearch.trim().toLowerCase();
    if (!term) return students;
    return students.filter((student) =>
      (student.name || "").toLowerCase().includes(term) ||
      (student.admissionNumber || "").toLowerCase().includes(term) ||
      (student.userCode || "").toLowerCase().includes(term) ||
      String(student.id).includes(term)
    );
  }, [students, offlineStudentSearch]);

  const inventorySummary = useMemo(() => {
    const totalItems = inventory.length;
    const totalStock = inventory.reduce((sum, item) => sum + Number(item.stock || 0), 0);
    const lowStock = inventory.filter((item) => Number(item.stock || 0) > 0 && Number(item.stock || 0) <= 5).length;
    const outOfStock = inventory.filter((item) => Number(item.stock || 0) <= 0).length;
    return { totalItems, totalStock, lowStock, outOfStock };
  }, [inventory]);

  const orderSummary = useMemo(() => {
    const totalOrders = orders.length;
    const pendingOrders = orders.filter((order) => String(order.status || "").toLowerCase() === "pending").length;
    const issuedOrders = orders.filter((order) => String(order.status || "").toLowerCase().includes("issued")).length;
    const unpaidOrders = orders.filter((order) => String(order.paymentStatus || "").toLowerCase() !== "paid").length;
    return { totalOrders, pendingOrders, issuedOrders, unpaidOrders };
  }, [orders]);

  const openCreateModal = () => {
    setEditingItem(null);
    setItemForm({
      name: "",
      description: "",
      size: "",
      buyingPrice: "",
      sellingPrice: "",
      stock: "",
      category: ""
    });
    setIsItemModalOpen(true);
  };

  const openEditModal = (item: InventoryItem) => {
    setEditingItem(item);
    setItemForm({
      name: item.name || "",
      description: item.description || "",
      size: item.size || "",
      buyingPrice: item.buyingPrice ? String(item.buyingPrice) : "",
      sellingPrice: item.sellingPrice ? String(item.sellingPrice) : item.price ? String(item.price) : "",
      stock: String(item.stock ?? 0),
      category: item.category || ""
    });
    setIsItemModalOpen(true);
  };

  const handleSaveItem = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        name: itemForm.name,
        description: itemForm.description,
        size: itemForm.size,
        buyingPrice: itemForm.buyingPrice ? Number(itemForm.buyingPrice) : null,
        sellingPrice: itemForm.sellingPrice ? Number(itemForm.sellingPrice) : null,
        price: itemForm.sellingPrice ? Number(itemForm.sellingPrice) : undefined,
        stock: Number(itemForm.stock || 0),
        category: itemForm.category
      };
      if (editingItem) {
        await axios.put(getApiUrl(`inventory/${editingItem.id}`), payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ type: "success", text: "Inventory item updated." });
      } else {
        await axios.post(getApiUrl("inventory"), payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ type: "success", text: "Inventory item created." });
      }
      setIsItemModalOpen(false);
      fetchInventory();
    } catch (err: any) {
      setMessage({ type: "error", text: err.response?.data?.message || "Failed to save item." });
    }
  };

  const handleApprove = async (orderId: number, approved: boolean) => {
    try {
      await axios.put(
        getApiUrl(`orders/${orderId}/approve`),
        { approved },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: "success", text: approved ? "Order approved." : "Order rejected." });
      fetchOrders();
    } catch (err: any) {
      setMessage({ type: "error", text: err.response?.data?.message || "Failed to update approval." });
    }
  };

  const handleIssueOrder = async (orderId: number) => {
    const targetOrder = orders.find((order) => order.id === orderId) || selectedOrder;
    if (!hasReachedIssueThreshold(targetOrder)) {
      const totalAmount = Number(targetOrder?.totalAmount || 0);
      const paidAmount = Number(targetOrder?.paidAmount || 0);
      setMessage({
        type: "error",
        text: `Issue is locked until at least 50% is paid. Paid K ${paidAmount.toFixed(2)} of K ${totalAmount.toFixed(2)}.`
      });
      return;
    }

    try {
      setActiveOrderAction({ orderId, type: "issuing" });
      setIsOrderModalOpen(false);
      setSelectedOrder(null);
      setMessage({ type: "success", text: `Marking order #${orderId} as issued...` });
      await axios.put(
        getApiUrl(`orders/${orderId}/status`),
        { status: "Issued/Collected" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: "success", text: "Order marked as issued." });
      await fetchOrders();
    } catch (err: any) {
      setMessage({ type: "error", text: err.response?.data?.message || "Failed to issue order." });
    } finally {
      setActiveOrderAction((current) => (
        current?.orderId === orderId && current.type === "issuing" ? null : current
      ));
    }
  };

  const handleRecordPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrder) return;

    const amount = Number(paymentForm.amount || 0);
    if (!amount || amount <= 0) {
      setMessage({ type: "error", text: "Enter a valid payment amount." });
      return;
    }

    try {
      setActiveOrderAction({ orderId: selectedOrder.id, type: "payment" });
      await axios.post(
        getApiUrl(`orders/${selectedOrder.id}/payment`),
        {
          amount,
          paymentMethod: paymentForm.paymentMethod,
          reference: paymentForm.reference.trim() || undefined,
          notes: paymentForm.notes.trim() || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage({ type: "success", text: "Payment recorded successfully." });
      await fetchOrders();
      setIsOrderModalOpen(false);
      setSelectedOrder(null);
      setPaymentForm({
        amount: "",
        paymentMethod: "Cash",
        reference: "",
        notes: ""
      });
    } catch (err: any) {
      setMessage({ type: "error", text: err.response?.data?.message || "Failed to record payment." });
    } finally {
      setActiveOrderAction((current) => (
        current?.orderId === selectedOrder.id && current.type === "payment" ? null : current
      ));
    }
  };

  const openOrderModal = (order: Order) => {
    setSelectedOrder(order);
    setPaymentForm({
      amount: String(Number(order.balance ?? order.totalAmount ?? 0).toFixed(2)),
      paymentMethod: order.paymentMethod || "Cash",
      reference: "",
      notes: ""
    });
    setIsOrderModalOpen(true);
  };

  const openOfflineOrderModal = async () => {
    try {
      if (inventory.length === 0) {
        await fetchInventory();
      }
      if (students.length === 0) {
        await fetchStudents();
      }
    } catch {
      setMessage({ type: "error", text: "Unable to prepare offline order form right now." });
    }
    setOfflineOrderForm({
      studentId: "",
      notes: "",
      items: [{ itemId: "", quantity: "1" }]
    });
    setOfflineStudentSearch("");
    setIsOfflineOrderModalOpen(true);
  };

  const updateOfflineOrderItem = (index: number, key: "itemId" | "quantity", value: string) => {
    setOfflineOrderForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => (
        itemIndex === index ? { ...item, [key]: value } : item
      ))
    }));
  };

  const addOfflineOrderItem = () => {
    setOfflineOrderForm((current) => ({
      ...current,
      items: [...current.items, { itemId: "", quantity: "1" }]
    }));
  };

  const removeOfflineOrderItem = (index: number) => {
    setOfflineOrderForm((current) => ({
      ...current,
      items: current.items.filter((_, itemIndex) => itemIndex !== index)
    }));
  };

  const handleCreateOfflineOrder = async (e: React.FormEvent) => {
    e.preventDefault();

    const studentId = Number(offlineOrderForm.studentId);
    const items = offlineOrderForm.items
      .map((item) => ({
        id: Number(item.itemId),
        quantity: Number(item.quantity)
      }))
      .filter((item) => item.id > 0 && item.quantity > 0);

    if (!studentId) {
      setMessage({ type: "error", text: "Select a student for this offline order." });
      return;
    }

    if (items.length === 0) {
      setMessage({ type: "error", text: "Add at least one inventory item to the order." });
      return;
    }

    try {
      setCreatingOfflineOrder(true);
      await axios.post(
        getApiUrl("orders"),
        {
          studentId,
          items,
          notes: offlineOrderForm.notes.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: "success", text: "Offline order recorded successfully." });
      setIsOfflineOrderModalOpen(false);
      await fetchOrders();
    } catch (err: any) {
      setMessage({ type: "error", text: err.response?.data?.message || "Failed to create offline order." });
    } finally {
      setCreatingOfflineOrder(false);
    }
  };

  return (
    <div className="dashboard-container">
      <main className="dashboard-main p-8 bg-slate-50/30">
        <div className="utopia-page-container">
          <div className="mb-6">
            <h1 className="text-2xl font-black tracking-tight text-slate-900 md:text-3xl">Uniforms Management</h1>
            <p className="mt-2 text-base text-slate-500">
              Review, approve, and issue uniform orders for learners.
            </p>
          </div>

          <div className="mb-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {(viewMode === "inventory"
              ? [
                  { label: "Inventory Items", value: inventorySummary.totalItems, helper: "Tracked catalog records", Icon: Package2, tone: "border-sky-100 bg-sky-50 text-sky-700" },
                  { label: "Units in Stock", value: inventorySummary.totalStock, helper: "Total available uniform units", Icon: Package2, tone: "border-emerald-100 bg-emerald-50 text-emerald-700" },
                  { label: "Low Stock", value: inventorySummary.lowStock, helper: "Items with 5 or fewer units", Icon: AlertTriangle, tone: "border-amber-100 bg-amber-50 text-amber-700" },
                  { label: "Out of Stock", value: inventorySummary.outOfStock, helper: "Items requiring replenishment", Icon: XCircle, tone: "border-rose-100 bg-rose-50 text-rose-700" }
                ]
              : [
                  { label: "Orders", value: orderSummary.totalOrders, helper: "Total uniform orders", Icon: ShoppingCart, tone: "border-sky-100 bg-sky-50 text-sky-700" },
                  { label: "Pending Approval", value: orderSummary.pendingOrders, helper: "Waiting for review", Icon: AlertTriangle, tone: "border-amber-100 bg-amber-50 text-amber-700" },
                  { label: "Issued", value: orderSummary.issuedOrders, helper: "Completed collections", Icon: CheckCircle2, tone: "border-emerald-100 bg-emerald-50 text-emerald-700" },
                  { label: "Payment Pending", value: orderSummary.unpaidOrders, helper: "Orders still unpaid", Icon: XCircle, tone: "border-rose-100 bg-rose-50 text-rose-700" }
                ]).map(({ label, value, helper, Icon, tone }) => (
              <article key={label} className="rounded-[2rem] border border-slate-100 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{label}</p>
                    <p className="mt-3 text-3xl font-black tracking-tight text-slate-900">{value}</p>
                    <p className="mt-2 text-sm text-slate-500">{helper}</p>
                  </div>
                  <div className={`rounded-2xl border p-3 ${tone}`}>
                    <Icon size={18} />
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="utopia-table-header">
            <div>
              <h2 className="text-xl font-extrabold text-slate-900">
                {viewMode === "orders" ? "Uniform Orders" : "Inventory Control"}
              </h2>
              <p className="page-subtitle text-slate-500">
                {viewMode === "orders"
                  ? "Track approvals, payment status, and issuance for learner orders."
                  : "Record inventory, receive stock, and maintain the school uniform catalog."}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative w-72">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  className="utopia-input pl-9 border-teal-100 focus:border-teal-500 focus:ring-teal-500/20"
                  placeholder={viewMode === "orders" ? "Search orders..." : "Search inventory..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
                <button
                  type="button"
                  onClick={() => setViewMode("orders")}
                  className={`rounded-xl px-4 py-2.5 text-[11px] font-black uppercase tracking-[0.18em] transition-all ${
                    viewMode === "orders" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50"
                  }`}
                >
                  Uniform Orders
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("inventory")}
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[11px] font-black uppercase tracking-[0.18em] transition-all ${
                    viewMode === "inventory" ? "bg-emerald-600 text-white shadow-lg shadow-emerald-600/20" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  }`}
                >
                  <Warehouse size={14} />
                  Record Inventory
                </button>
              </div>
              {viewMode === "orders" && (
                <>
                  <select
                    className="utopia-input min-w-0 text-sm cursor-pointer"
                    style={{ width: "96px" }}
                    value={orderStatusFilter}
                    onChange={(e) => setOrderStatusFilter(e.target.value)}
                  >
                    <option value="all">All orders</option>
                    <option value="pending">Pending approval</option>
                    <option value="approved">Approved</option>
                    <option value="issued">Issued</option>
                    <option value="payment_pending">Payment pending</option>
                  </select>
                  <button
                    type="button"
                    className="utopia-btn-primary bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 flex items-center gap-2"
                    onClick={() => {
                      openOfflineOrderModal().catch(() => undefined);
                    }}
                  >
                    <Plus size={16} />
                    Record Offline Order
                  </button>
                </>
              )}
              {viewMode === "inventory" && (
                <>
                  <button className="utopia-btn-primary bg-teal-600 hover:bg-teal-700 text-white px-5 py-2.5 flex items-center gap-2" onClick={openCreateModal}>
                    <Plus size={16} /> Record Inventory Item
                  </button>
                </>
              )}
            </div>
          </div>

          {message && (
            <div className={`mb-4 rounded-lg border px-4 py-3 text-sm ${message.type === "success" ? "border-emerald-100 bg-emerald-50 text-emerald-700" : "border-rose-100 bg-rose-50 text-rose-700"}`}>
              <div className="flex items-center gap-2">
                {activeOrderAction ? <Loader2 size={16} className="animate-spin" /> : null}
                <span>{message.text}</span>
              </div>
            </div>
          )}

          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : viewMode === "inventory" ? (
            <table className="utopia-table text-[15px]">
              <thead>
                <tr className="bg-slate-50 text-slate-600 uppercase text-xs tracking-widest">
                  <th className="py-4">No.</th>
                  <th className="py-4">Description</th>
                  <th className="py-4">Size</th>
                  <th className="py-4">Buying Price</th>
                  <th className="py-4">Selling Price</th>
                  <th className="py-4">Quantity in Stock</th>
                  <th className="py-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredInventory.map((item, idx) => (
                  <tr key={item.id} className="hover:bg-teal-50/40 transition-colors">
                    <td className="font-semibold text-slate-700">{idx + 1}</td>
                    <td className="font-bold text-slate-800">{item.name}</td>
                    <td className="text-slate-600">{item.size || "-"}</td>
                    <td className="text-slate-600">K {Number(item.buyingPrice ?? 0).toFixed(2)}</td>
                    <td className="text-slate-700 font-semibold">K {Number(item.sellingPrice ?? item.price ?? 0).toFixed(2)}</td>
                    <td className="text-slate-700 font-semibold">{item.stock ?? 0}</td>
                    <td className="text-right">
                      <div className="flex justify-end gap-2">
                        <button className="utopia-btn-icon text-slate-600" onClick={() => openEditModal(item)}>
                          <Edit2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredInventory.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-slate-400 py-8">
                      No inventory items match the current search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <table className="utopia-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Class/Grade</th>
                  <th>Order Date</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Payment</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleOrders.map((order) => (
                  <tr key={order.id} className="group">
                    <td className="font-bold text-slate-700">{order.studentName || "Student"}</td>
                    <td className="text-slate-500">{order.className || "-"}</td>
                    <td className="text-slate-500">{order.createdAt ? new Date(order.createdAt).toLocaleDateString() : "-"}</td>
                    <td className="font-semibold text-slate-700">K {Number(order.totalAmount || 0).toFixed(2)}</td>
                    <td>
                      <span className={`utopia-badge text-[9px] font-black uppercase ${getOrderStatusBadgeClass(order.status)}`}>
                        {order.status || "Pending"}
                      </span>
                    </td>
                    <td>
                      <span className={`utopia-badge text-[9px] font-black uppercase ${getPaymentStatusBadgeClass(order.paymentStatus)}`}>
                        {order.paymentStatus || "Pending"}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button className="utopia-btn-icon text-slate-600" onClick={() => openOrderModal(order)}>
                          <Eye size={16} />
                        </button>
                        <button className="utopia-btn-icon text-rose-600" onClick={() => handleApprove(order.id, false)}>
                          <XCircle size={16} />
                        </button>
                        <button
                          className="utopia-btn-icon text-blue-600 disabled:opacity-60"
                          onClick={() => handleIssueOrder(order.id)}
                          disabled={activeOrderAction?.orderId === order.id || !hasReachedIssueThreshold(order)}
                          title={hasReachedIssueThreshold(order) ? "Mark as issued" : "Pay at least half before issuing"}
                        >
                          {activeOrderAction?.orderId === order.id && activeOrderAction.type === "issuing" ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            "Issue"
                          )}
                        </button>
                        <button
                          className="utopia-btn-icon text-emerald-700 disabled:opacity-60"
                          onClick={() => openOrderModal(order)}
                          disabled={activeOrderAction?.orderId === order.id}
                          title="Record payment"
                        >
                          <CreditCard size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {visibleOrders.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-slate-400 py-8">
                      No orders match the current search or status filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {isItemModalOpen && (
        <div className="utopia-modal-overlay" onClick={(e) => e.target === e.currentTarget && setIsItemModalOpen(false)}>
          <div className="utopia-modal-content max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="utopia-modal-header">
              <h3 className="text-lg font-bold">{editingItem ? "Edit Item" : "Add New Item"}</h3>
            </div>
            <form onSubmit={handleSaveItem} className="space-y-4">
              <div className="utopia-form-group">
                <label className="utopia-label required">Description</label>
                <input className="utopia-input" value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} required />
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label">Size</label>
                <input className="utopia-input" value={itemForm.size} onChange={(e) => setItemForm({ ...itemForm, size: e.target.value })} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="utopia-form-group">
                  <label className="utopia-label">Buying Price</label>
                  <input type="number" className="utopia-input" value={itemForm.buyingPrice} onChange={(e) => setItemForm({ ...itemForm, buyingPrice: e.target.value })} />
                </div>
                <div className="utopia-form-group">
                  <label className="utopia-label required">Selling Price</label>
                  <input type="number" className="utopia-input" value={itemForm.sellingPrice} onChange={(e) => setItemForm({ ...itemForm, sellingPrice: e.target.value })} required />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="utopia-form-group">
                  <label className="utopia-label required">Stock</label>
                  <input type="number" className="utopia-input" value={itemForm.stock} onChange={(e) => setItemForm({ ...itemForm, stock: e.target.value })} required />
                </div>
                <div className="utopia-form-group">
                  <label className="utopia-label required">Category</label>
                  <input className="utopia-input" value={itemForm.category} onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })} required />
                </div>
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label">Notes</label>
                <textarea className="utopia-textarea" value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} />
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" className="utopia-btn-secondary" onClick={() => setIsItemModalOpen(false)}>Cancel</button>
                <button type="submit" className="utopia-btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isOfflineOrderModalOpen && (
        <div className="utopia-modal-overlay" onClick={(e) => e.target === e.currentTarget && setIsOfflineOrderModalOpen(false)}>
          <div className="utopia-modal-content max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="utopia-modal-header">
              <h3 className="text-lg font-bold">Record Offline Order</h3>
              <p>Capture an in-person uniform order and attach it to the correct student record.</p>
            </div>
            <form onSubmit={handleCreateOfflineOrder} className="space-y-5">
              <div className="utopia-form-group">
                <label className="utopia-label required">Student</label>
                <div className="relative mb-3">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    className="utopia-input pl-9"
                    value={offlineStudentSearch}
                    onChange={(e) => setOfflineStudentSearch(e.target.value)}
                    placeholder="Search by learner name, admission number, user code, or ID"
                  />
                </div>
                <select
                  className="utopia-input"
                  value={offlineOrderForm.studentId}
                  onChange={(e) => setOfflineOrderForm((current) => ({ ...current, studentId: e.target.value }))}
                  required
                >
                  <option value="">Select student...</option>
                  {filteredOfflineStudents.map((student) => (
                    <option key={student.id} value={student.id}>
                      {student.name}
                      {student.admissionNumber ? ` - ${student.admissionNumber}` : student.userCode ? ` - ${student.userCode}` : ""}
                    </option>
                  ))}
                </select>
                {offlineStudentSearch.trim() && filteredOfflineStudents.length === 0 ? (
                  <p className="mt-2 text-xs text-slate-500">No learners match that search.</p>
                ) : null}
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="utopia-label required !mb-0">Items</label>
                  <button type="button" className="utopia-btn-secondary" onClick={addOfflineOrderItem}>
                    <Plus size={16} /> Add Item
                  </button>
                </div>
                {offlineOrderForm.items.map((item, index) => {
                  const availableItems = inventory.filter((inventoryItem) => Number(inventoryItem.stock || 0) > 0);
                  return (
                    <div key={`offline-item-${index}`} className="grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 p-4 md:grid-cols-[1fr_140px_auto]">
                      <select
                        className="utopia-input"
                        value={item.itemId}
                        onChange={(e) => updateOfflineOrderItem(index, "itemId", e.target.value)}
                        required
                      >
                        <option value="">Select inventory item...</option>
                        {availableItems.map((inventoryItem) => (
                          <option key={inventoryItem.id} value={inventoryItem.id}>
                            {inventoryItem.name} - K {Number(inventoryItem.sellingPrice ?? inventoryItem.price ?? 0).toFixed(2)} ({inventoryItem.stock} in stock)
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="1"
                        className="utopia-input"
                        value={item.quantity}
                        onChange={(e) => updateOfflineOrderItem(index, "quantity", e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        className="utopia-btn-secondary"
                        onClick={() => removeOfflineOrderItem(index)}
                        disabled={offlineOrderForm.items.length === 1}
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="utopia-form-group">
                <label className="utopia-label">Notes</label>
                <textarea
                  className="utopia-textarea"
                  value={offlineOrderForm.notes}
                  onChange={(e) => setOfflineOrderForm((current) => ({ ...current, notes: e.target.value }))}
                  placeholder="Optional note for walk-in or offline order details"
                />
              </div>

              <div className="flex justify-end gap-3">
                <button type="button" className="utopia-btn-secondary" onClick={() => setIsOfflineOrderModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="utopia-btn-primary inline-flex items-center gap-2" disabled={creatingOfflineOrder}>
                  {creatingOfflineOrder ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                  {creatingOfflineOrder ? "Saving..." : "Create Order"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isOrderModalOpen && selectedOrder && (
        <div className="utopia-modal-overlay" onClick={(e) => e.target === e.currentTarget && setIsOrderModalOpen(false)}>
          <div className="utopia-modal-content max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="utopia-modal-header">
              <h3 className="text-lg font-bold">Order Details</h3>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-slate-500">Student:</span> <span className="font-semibold">{selectedOrder.studentName || "Student"}</span></div>
                <div><span className="text-slate-500">Class:</span> <span className="font-semibold">{selectedOrder.className || "-"}</span></div>
                <div><span className="text-slate-500">Status:</span> <span className="font-semibold">{selectedOrder.status || "Pending"}</span></div>
                <div><span className="text-slate-500">Payment:</span> <span className="font-semibold">{selectedOrder.paymentStatus || "Unpaid"}</span></div>
              </div>
              <div className="border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2">Item</th>
                      <th className="text-left px-4 py-2">Size</th>
                      <th className="text-center px-4 py-2">Qty</th>
                      <th className="text-right px-4 py-2">Unit</th>
                      <th className="text-right px-4 py-2">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parseItems(selectedOrder.item).map((item: any, idx: number) => (
                      <tr key={`${selectedOrder.id}-${idx}`} className="border-t">
                        <td className="px-4 py-2">{item.itemName || item.name}</td>
                        <td className="px-4 py-2">{item.size || "-"}</td>
                        <td className="px-4 py-2 text-center">{item.quantity}</td>
                        <td className="px-4 py-2 text-right">K {Number(item.unitPrice || 0).toFixed(2)}</td>
                        <td className="px-4 py-2 text-right">K {Number(item.totalPrice || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm md:grid-cols-3">
                <div>
                  <p className="text-slate-500">Total</p>
                  <p className="mt-1 font-semibold text-slate-900">K {Number(selectedOrder.totalAmount || 0).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Paid</p>
                  <p className="mt-1 font-semibold text-emerald-700">K {Number(selectedOrder.paidAmount || 0).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Balance</p>
                  <p className="mt-1 font-semibold text-rose-700">K {Number(selectedOrder.balance ?? selectedOrder.totalAmount ?? 0).toFixed(2)}</p>
                </div>
              </div>
              <form onSubmit={handleRecordPayment} className="space-y-4 rounded-2xl border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h4 className="font-semibold text-slate-900">Record Payment</h4>
                    <p className="text-sm text-slate-500">Use this to mark the order as partially or fully paid.</p>
                  </div>
                  <CreditCard size={18} className="text-emerald-600" />
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="utopia-form-group">
                    <label className="utopia-label required">Amount</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="utopia-input"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                      required
                    />
                  </div>
                  <div className="utopia-form-group">
                    <label className="utopia-label required">Payment Method</label>
                    <select
                      className="utopia-input"
                      value={paymentForm.paymentMethod}
                      onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                    >
                      <option value="Cash">Cash</option>
                      <option value="Mobile Money">Mobile Money</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Card">Card</option>
                    </select>
                  </div>
                </div>
                <div className="utopia-form-group">
                  <label className="utopia-label">Reference</label>
                  <input
                    className="utopia-input"
                    value={paymentForm.reference}
                    onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                    placeholder="Receipt number, transaction id, or voucher"
                  />
                </div>
                <div className="utopia-form-group">
                  <label className="utopia-label">Notes</label>
                  <textarea
                    className="utopia-textarea"
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    placeholder="Optional payment note"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="utopia-btn-primary inline-flex items-center gap-2"
                    disabled={activeOrderAction?.orderId === selectedOrder.id}
                  >
                    {activeOrderAction?.orderId === selectedOrder.id && activeOrderAction.type === "payment" ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Recording...
                      </>
                    ) : (
                      <>
                        <CreditCard size={16} />
                        Record Payment
                      </>
                    )}
                  </button>
                </div>
              </form>
              {!hasReachedIssueThreshold(selectedOrder) && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  At least 50% of the total must be paid before this order can be marked as issued.
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button className="utopia-btn-secondary" onClick={() => setIsOrderModalOpen(false)}>Close</button>
                <button className="utopia-btn-secondary" onClick={() => window.print()}>Print</button>
                <button
                  className="utopia-btn-primary inline-flex items-center gap-2"
                  onClick={() => handleIssueOrder(selectedOrder.id)}
                  disabled={activeOrderAction?.orderId === selectedOrder.id || !hasReachedIssueThreshold(selectedOrder)}
                >
                  {activeOrderAction?.orderId === selectedOrder.id && activeOrderAction.type === "issuing" ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Issuing...
                    </>
                  ) : (
                    "Mark as Issued"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UniformsManagement;
